import React, {useEffect, useMemo, useState} from "react";
import {useNavigate, useOutletContext, useParams, useSearchParams} from "react-router-dom";
import api from "../../../../../lib/api";
import Button from "../../../../../components/Button";
import {notify} from "../../../../../components/Notifications";
import {getErrMsg} from "../../../../../lib/errors";
import {useBusiness} from "../../../../../context/BusinessContext";
import {
    AlertTriangleIcon,
    CalendarIcon,
    CheckCircle2Icon,
    EyeIcon,
    ImageIcon,
    MapPinIcon,
    RocketIcon,
    TicketIcon,
    Undo2Icon,
    UsersIcon,
} from "lucide-react";
import {MarkdownDisplay} from "../../../../../components/MarkdownDisplay";
import SectionHeading from "../../../../../components/SectionHeading";

type OutletCtx = { eventId: string | null };

function cx(...c: Array<string | false | null | undefined>) {
    return c.filter(Boolean).join(" ");
}

function formatMoney(n: number) {
    return new Intl.NumberFormat("en-NA", {minimumFractionDigits: 2, maximumFractionDigits: 2}).format(n);
}

function formatDateTime(iso?: string | null) {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit"
    });
}

function Pill({ok, label}: { ok: boolean; label: string }) {
    return (
        <span
            className={cx(
                "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ring-1",
                ok ? "bg-emerald-50 text-emerald-700 ring-emerald-100" : "bg-gray-100 text-gray-700 ring-gray-200"
            )}
        >
      {label}
    </span>
    );
}

function Card({
                  title,
                  hint,
                  children,
                  icon,
                  right,
              }: {
    title: string;
    hint?: string;
    children: React.ReactNode;
    icon?: React.ReactNode;
    right?: React.ReactNode;
}) {
    return (
        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            <div className="flex items-start justify-between gap-6 border-b border-gray-200 bg-gray-50 px-5 py-4">
                <div className="min-w-0">
                    <div className="flex items-center gap-2">
                        {icon}
                        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
                    </div>
                    {hint ? <p className="mt-1 text-xs text-gray-500">{hint}</p> : null}
                </div>
                {right}
            </div>
            <div className="p-5">{children}</div>
        </div>
    );
}

type Ticket = {
    id?: string;
    name: string;
    price: number;
    capacity?: number | null;
    display?: boolean;
    earlyBird?: boolean;
    startDate?: string | null;
    endDate?: string | null;
    maxPerOrder?: number | null;
    position?: number | null;
};

type RefundPolicy = {
    allowed: boolean;
    rate: number;
    deadlineDays: number;
    description?: string | null;
};

type ReviewChecks = {
    hasDetails: boolean;
    hasListingImage: boolean;
    hasTickets: boolean;
    hasRefundPolicy: boolean;
    readyToPublish: boolean;
    missing: string[];
};

type ReviewResponse = {
    id: string;
    title: string;
    slug?: string;
    category?: string;
    description?: string | null;
    startDate?: string | null;
    endDate?: string | null;
    locationType?: string | null;
    location?: string | null;
    address?: string | null;
    virtualPlatform?: string | null;
    virtualLink?: string | null;

    published?: boolean;
    seatmapEnabled?: boolean;

    // images
    imageUrl?: string | null;
    coverImageUrl?: string | null;

    // tickets
    tickets?: Ticket[];
    ticketCount?: number;
    visibleTicketCount?: number;

    // data collection flags
    collectAttendeeDetailsForEachTicket?: boolean;
    collectMedicalAid?: boolean;
    collectEmergencyContact?: boolean;
    waiverRequired?: boolean;
    collectInvoiceDetails?: boolean;
    membershipLookupRequired?: boolean;
    singleTicketPerOrder?: boolean;
    waiverText?: string | null;

    // custom fields
    customFieldCount?: number;

    // refund policy
    refundPolicy?: RefundPolicy | null;

    // mailing
    confirmationEmailText?: string | null;
    complementaryTicketEmailText?: string | null;
    invoiceEmailText?: string | null;

    // server-computed readiness
    checks?: ReviewChecks | null;
};

function checklistFrom(ev: ReviewResponse) {
    const s = ev.checks;
    const tickets = ev.tickets ?? [];

    const invalidSaleWindow = tickets.some((t) => {
        if (!t.startDate || !t.endDate) return false;
        const a = new Date(t.startDate).getTime();
        const b = new Date(t.endDate).getTime();
        return Number.isFinite(a) && Number.isFinite(b) && a > b;
    });

    const detailsOk = s?.hasDetails ?? (
        !!ev.title?.trim() && !!ev.category?.trim() && !!ev.startDate && !!ev.endDate && !!ev.location?.trim()
    );
    const hasListingImage = s?.hasListingImage ?? !!(ev.imageUrl && String(ev.imageUrl).trim().length > 0);
    const refundsOk = s?.hasRefundPolicy ?? (
        ev.refundPolicy ? !ev.refundPolicy.allowed || (ev.refundPolicy.rate >= 0 && ev.refundPolicy.deadlineDays >= 0) : true
    );
    const waiverOk = !ev.waiverRequired || !!(ev.waiverText && ev.waiverText.trim().length > 0);
    const seatmapOk =
        !ev.seatmapEnabled ||
        tickets.every((t: any) => {
            const c = (t as any).category;
            return c != null && String(c).trim().length > 0;
        });
    const readyToPublish = s?.readyToPublish ?? (detailsOk && hasListingImage && refundsOk && waiverOk && seatmapOk);
    const missing = s?.missing ?? [];

    return { detailsOk, hasListingImage, refundsOk, waiverOk, seatmapOk, invalidSaleWindow, readyToPublish, missing };
}

export function ReviewPublishStep({mode}: { mode: "create" | "edit" }) {
    const {activeBusiness} = useBusiness();
    const businessId = activeBusiness?.id ?? null;

    const navigate = useNavigate();
    const params = useParams();
    const [sp] = useSearchParams();
    const outlet = useOutletContext<OutletCtx>();

    const eventId =
        mode === "edit"
            ? (params.eventId ?? outlet.eventId ?? null)
            : (outlet.eventId ?? sp.get("eventId") ?? null);

    const [loading, setLoading] = useState(false);
    const [publishing, setPublishing] = useState(false);

    const [ev, setEv] = useState<ReviewResponse | null>(null);

    const refresh = async () => {
        if (!businessId || !eventId) return;
        setLoading(true);
        try {
            const {data} = await api.get(`/api/v1/business/${businessId}/events/${eventId}/review`);

            if (data.tickets?.length) {
                data.tickets.sort((a: Ticket, b: Ticket) => {
                    const posA = a.position ?? Number.POSITIVE_INFINITY;
                    const posB = b.position ?? Number.POSITIVE_INFINITY;
                    if (posA !== posB) return posA - posB;
                });
            }

            setEv(data);
        } catch (e) {
            notify.error(getErrMsg(e, "Failed to load review"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!businessId || !eventId) return;
        void refresh();
    }, [businessId, eventId]);

    // speaker and slot counts come from their own endpoints; the review response does not carry them
    const [programme, setProgramme] = useState<{ speakers: number; slots: number } | null>(null);
    useEffect(() => {
        if (!businessId || !eventId) return;
        const base = `/api/v2/business/${businessId}/events/${eventId}`;
        Promise.all([api.get(`${base}/speakers`), api.get(`${base}/lineup`)])
            .then(([sp, lu]) => setProgramme({
                speakers: Array.isArray(sp.data) ? sp.data.length : 0,
                slots: Array.isArray(lu.data) ? lu.data.length : 0,
            }))
            .catch(() => setProgramme(null));
    }, [businessId, eventId]);

    const checks = useMemo(() => (ev ? checklistFrom(ev) : null), [ev]);

    const canPublish = useMemo(() => {
        if (!checks) return false;
        return checks.readyToPublish && checks.waiverOk && checks.seatmapOk;
    }, [checks]);

    const publish = async () => {
        if (!businessId || !eventId) return;

        if (!canPublish) {
            notify.error("Please fix the required items before publishing.");
            return;
        }

        setPublishing(true);
        try {
            await api.post(`/api/v1/business/${businessId}/events/${eventId}/publish`);
            notify.success("Event published");
            await refresh();
        } catch (e) {
            notify.error(getErrMsg(e, "Failed to publish event"));
        } finally {
            setPublishing(false);
        }
    };

    const unpublish = async () => {
        if (!businessId || !eventId) return;
        setPublishing(true);
        try {
            await api.post(`/api/v1/business/${businessId}/events/${eventId}/publish`);
            notify.success("Event unpublished");
            await refresh();
        } catch (e) {
            notify.error(getErrMsg(e, "Failed to unpublish event"));
        } finally {
            setPublishing(false);
        }
    };

    const openPublicPreview = () => {
        if (!ev?.slug) {
            notify.error("No event slug available for preview.");
            return;
        }
        window.open(`/explore/${encodeURIComponent(ev.slug)}`, "_blank", "noopener,noreferrer");
    };

    if (!eventId) {
        return (
            <div className="rounded-xl border border-gray-200 bg-white p-6 text-sm text-gray-600">
                Please complete <span className="font-medium">Event Details</span> first.
            </div>
        );
    }

    return (
        <div className="space-y-6">

            <SectionHeading
                title={mode === "edit" ? "Review your event details" : "Your event is almost ready"}
                description={mode === "edit" ? "Make sure everything looks good before publishing. You can always come back and edit later." : "Review everything before publishing. You can always come back and edit later."}
                actions={
                    <>
                        <Button type="button" variant="outline" size="md"
                                onClick={() => navigate("/business/events")}>
                            Exit
                        </Button>

                        <Button type="button" variant="secondary" size="md" onClick={refresh}
                                disabled={loading || publishing}>
                            Refresh
                        </Button>

                        <Button type="button" variant="secondary" size="md" onClick={openPublicPreview}
                                disabled={!ev?.slug}>
                                <span className="inline-flex items-center gap-2">
                                  <EyeIcon className="size-4"/>
                                  Preview
                                </span>
                        </Button>

                        {ev?.published ? (
                            <Button type="button" variant="primary" size="md" onClick={unpublish} disabled={publishing}>
                            <span className="inline-flex items-center gap-2">
                              <Undo2Icon className="size-4"/>
                              Unpublish
                            </span>
                            </Button>
                        ) : (
                            <Button type="button" variant="primary" size="md" onClick={publish}
                                    disabled={publishing || loading || !canPublish}>
                              <span className="inline-flex items-center gap-2">
                                <RocketIcon className="size-4"/>
                                  {publishing ? "Publishing…" : "Publish event"}
                              </span>
                            </Button>
                        )}
                    </>
                }
            />
            
            {loading || !ev || !checks ? (
                <div className="py-10 text-sm text-gray-500">Loading review…</div>
            ) : (
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
                    {/* Left: summary */}
                    <div className="lg:col-span-7 space-y-6">
                        <Card
                            title="Event summary"
                            icon={<CheckCircle2Icon className="size-4 text-gray-500"/>}
                            right={<Pill ok={!!ev.published} label={ev.published ? "Published" : "Draft"}/>}
                        >
                            <div className="space-y-4">
                                <div className="text-lg font-semibold text-gray-900">{ev.title}</div>

                                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                    <div className="flex items-start gap-2 text-sm text-gray-700">
                                        <CalendarIcon className="mt-0.5 size-4 text-gray-400"/>
                                        <div>
                                            <div className="font-medium">Start</div>
                                            <div className="text-gray-600">{ev.startDate}</div>
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-2 text-sm text-gray-700">
                                        <CalendarIcon className="mt-0.5 size-4 text-gray-400"/>
                                        <div>
                                            <div className="font-medium">End</div>
                                            <div className="text-gray-600">{ev.endDate}</div>
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-2 text-sm text-gray-700">
                                        <MapPinIcon className="mt-0.5 size-4 text-gray-400"/>
                                        <div>
                                            {ev.locationType === "VIRTUAL" ? (
                                                <>
                                                    <div className="font-medium">Virtual event</div>
                                                    {ev.virtualPlatform && <div className="text-gray-600">{ev.virtualPlatform}</div>}
                                                    {ev.virtualLink && <div className="text-gray-500 text-xs truncate">{ev.virtualLink}</div>}
                                                </>
                                            ) : ev.locationType === "HYBRID" ? (
                                                <>
                                                    <div className="font-medium">{ev.location || "—"} <span className="text-xs font-normal text-gray-500">(+ virtual)</span></div>
                                                    <div className="text-gray-600">{ev.address || "—"}</div>
                                                    {ev.virtualPlatform && <div className="text-gray-500 text-xs">{ev.virtualPlatform}</div>}
                                                </>
                                            ) : (
                                                <>
                                                    <div className="font-medium">{ev.location || "—"}</div>
                                                    <div className="text-gray-600">{ev.address || "—"}</div>
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-2 text-sm text-gray-700">
                                        <TicketIcon className="mt-0.5 size-4 text-gray-400"/>
                                        <div>
                                            <div className="font-medium">Category</div>
                                            <div className="text-gray-600">{ev.category}</div>
                                        </div>
                                    </div>
                                </div>

                                {ev.description ? (
                                    <div
                                        className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-800">
                                        <MarkdownDisplay value={ev.description || "Nothing to preview"}/>
                                    </div>
                                ) : null}
                            </div>
                        </Card>

                        <Card
                            title="Tickets"
                            icon={<TicketIcon className="size-4 text-gray-500"/>}
                        >
                            {ev.tickets?.length ? (
                                <div className="space-y-3">
                                    {checks.invalidSaleWindow ? (
                                        <div
                                            className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                                            <div className="inline-flex items-center gap-2 font-semibold">
                                                <AlertTriangleIcon className="size-4"/>
                                                Some tickets have an invalid sale window (start after end).
                                            </div>
                                        </div>
                                    ) : null}

                                    <div className="rounded-xl border border-gray-200 overflow-hidden">
                                        <div
                                            className="grid grid-cols-12 gap-2 bg-gray-50 px-4 py-2 text-xs font-semibold text-gray-600">
                                            <div className="col-span-5">Ticket</div>
                                            <div className="col-span-2">Price</div>
                                            <div className="col-span-2">Capacity</div>
                                            <div className="col-span-3">Status</div>
                                        </div>
                                        <div className="divide-y divide-gray-100">
                                            {ev.tickets.map((t, idx) => (
                                                <div key={t.id ?? idx}
                                                     className="grid grid-cols-12 gap-2 px-4 py-3 text-sm">
                                                    <div className="col-span-5">
                                                        <div className="font-semibold text-gray-900">{t.name}</div>
                                                        <div className="mt-1 flex flex-wrap gap-2">
                                                            {t.earlyBird ? (
                                                                <span
                                                                    className="rounded-full bg-orange-50 px-2 py-0.5 text-xs font-semibold text-orange-700 ring-1 ring-orange-100">
                                                                    Early bird
                                                                </span>
                                                            ) : null}
                                                            {t.display ? <Pill ok={true} label="Displayed"/> :
                                                                <Pill ok={false} label="Hidden"/>}
                                                        </div>
                                                    </div>
                                                    <div
                                                        className="col-span-2 text-gray-700">N$ {formatMoney(t.price ?? 0)}</div>

                                                    <div className="col-span-2 text-gray-700">
                                                        {t.capacity == null || t.capacity === 0 ? "Unlimited" : t.capacity}
                                                        <div
                                                            className="text-xs text-gray-500">Max/order: {t.maxPerOrder ?? "-"}</div>
                                                    </div>

                                                    <div className="col-span-3 text-xs text-gray-600">
                                                        <div>Sale start: {formatDateTime(t.startDate ?? null)}</div>
                                                        <div>Sale end: {formatDateTime(t.endDate ?? null)}</div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="flex justify-end">
                                        <Button
                                            type="button"
                                            variant="secondary"
                                            size="md"
                                            onClick={() =>
                                                mode === "edit"
                                                    ? navigate(`/business/events/${encodeURIComponent(eventId)}/edit/tickets`)
                                                    : navigate(`/business/events/new/tickets?eventId=${encodeURIComponent(eventId)}`)
                                            }
                                        >
                                            Edit tickets
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
                                    No tickets yet.
                                    <div className="mt-3">
                                        <Button
                                            type="button"
                                            variant="primary"
                                            size="md"
                                            onClick={() =>
                                                mode === "edit"
                                                    ? navigate(`/business/events/${encodeURIComponent(eventId)}/edit/tickets`)
                                                    : navigate(`/business/events/new/tickets?eventId=${encodeURIComponent(eventId)}`)
                                            }
                                        >
                                            Add tickets
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </Card>

                        <Card
                            title="Images"
                            hint="A listing image is required to publish."
                            icon={<ImageIcon className="size-4 text-gray-500"/>}
                            right={<Pill ok={checks.hasListingImage}
                                         label={checks.hasListingImage ? "Ready" : "Missing"}/>}
                        >
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                <div>
                                    <div className="text-xs font-semibold text-gray-500">Listing image (required)</div>
                                    <div
                                        className="mt-2 overflow-hidden rounded-xl border border-gray-200 bg-gray-50 aspect-square">
                                        {ev.imageUrl ? (
                                            <img src={ev.imageUrl} alt="Listing"
                                                 className="h-full w-full object-cover"/>
                                        ) : (
                                            <div
                                                className="flex h-full items-center justify-center text-sm text-gray-500">Missing</div>
                                        )}
                                    </div>
                                </div>

                                <div>
                                    <div className="text-xs font-semibold text-gray-500">Cover image (optional)</div>
                                    <div
                                        className="mt-2 overflow-hidden rounded-xl border border-gray-200 bg-gray-50 h-48">
                                        {ev.coverImageUrl ? (
                                            <img src={ev.coverImageUrl} alt="Cover"
                                                 className="h-full w-full object-cover"/>
                                        ) : (
                                            <div
                                                className="flex h-full items-center justify-center text-sm text-gray-500">None</div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="mt-4 flex justify-end">
                                <Button
                                    type="button"
                                    variant="secondary"
                                    size="md"
                                    onClick={() =>
                                        mode === "edit"
                                            ? navigate(`/business/events/${encodeURIComponent(eventId)}/edit/images`)
                                            : navigate(`/business/events/new/images?eventId=${encodeURIComponent(eventId)}`)
                                    }
                                >
                                    Edit images
                                </Button>
                            </div>
                        </Card>
                    </div>

                    {/* Right: checklist + policies */}
                    <div className="lg:col-span-5 space-y-6">
                        <Card
                            title="Publish checklist"
                            hint="These items must be complete to publish."
                            icon={<CheckCircle2Icon className="size-4 text-gray-500"/>}
                        >
                            <div className="space-y-3">
                                <div className="flex items-start justify-between gap-4">
                                    <div>
                                        <div className="text-sm font-semibold text-gray-900">Event details</div>
                                        <div className="text-xs text-gray-500">Title, category, dates, venue & address
                                        </div>
                                    </div>
                                    <Pill ok={checks.detailsOk} label={checks.detailsOk ? "OK" : "Fix"}/>
                                </div>

                                <div className="flex items-start justify-between gap-4">
                                    <div>
                                        <div className="text-sm font-semibold text-gray-900">Listing image</div>
                                        <div className="text-xs text-gray-500">Square image shown in event listing</div>
                                    </div>
                                    <Pill ok={checks.hasListingImage} label={checks.hasListingImage ? "OK" : "Fix"}/>
                                </div>

                                <div className="flex items-start justify-between gap-4">
                                    <div>
                                        <div className="text-sm font-semibold text-gray-900">Refund policy</div>
                                        <div className="text-xs text-gray-500">Must be configured (or refunds
                                            disabled)
                                        </div>
                                    </div>
                                    <Pill ok={checks.refundsOk} label={checks.refundsOk ? "OK" : "Fix"}/>
                                </div>

                                <div className="flex items-start justify-between gap-4">
                                    <div>
                                        <div className="text-sm font-semibold text-gray-900">Waiver</div>
                                        <div className="text-xs text-gray-500">Required waiver text if waiver is
                                            enabled
                                        </div>
                                    </div>
                                    <Pill ok={checks.waiverOk} label={checks.waiverOk ? "OK" : "Fix"}/>
                                </div>

                                <div className="flex items-start justify-between gap-4">
                                    <div>
                                        <div className="text-sm font-semibold text-gray-900">Seat map mapping</div>
                                        <div className="text-xs text-gray-500">Ticket category required when seat map is
                                            enabled
                                        </div>
                                    </div>
                                    <Pill ok={checks.seatmapOk} label={checks.seatmapOk ? "OK" : "Fix"}/>
                                </div>

                                {!canPublish ? (
                                    <div
                                        className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                                        <div className="inline-flex items-center gap-2 font-semibold">
                                            <AlertTriangleIcon className="size-4"/>
                                            Publishing is blocked until required items are complete.
                                        </div>
                                        {checks.missing.length > 0 && (
                                            <ul className="mt-2 list-disc list-inside text-xs space-y-0.5">
                                                {checks.missing.map((m) => <li key={m}>{m}</li>)}
                                            </ul>
                                        )}
                                    </div>
                                ) : (
                                    <div
                                        className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
                                        <div className="inline-flex items-center gap-2 font-semibold">
                                            <CheckCircle2Icon className="size-4"/>
                                            Ready to publish.
                                        </div>
                                    </div>
                                )}
                            </div>
                        </Card>

                        <Card
                            title="Refund policy"
                            icon={<CheckCircle2Icon className="size-4 text-gray-500"/>}
                            right={<Pill ok={checks.refundsOk}
                                         label={checks.refundsOk ? "Configured" : "Needs attention"}/>}
                        >
                            {ev.refundPolicy ? (
                                <div className="space-y-2 text-sm text-gray-700">
                                    <div className="flex items-center justify-between">
                                        <div className="font-semibold">Refunds allowed</div>
                                        <div>{ev.refundPolicy.allowed ? "Yes" : "No"}</div>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <div className="font-semibold">Refund rate</div>
                                        <div>{ev.refundPolicy.allowed ? `${Math.round(ev.refundPolicy.rate)}%` : "—"}</div>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <div className="font-semibold">Deadline</div>
                                        <div>{ev.refundPolicy.allowed ? `${ev.refundPolicy.deadlineDays} days before` : "—"}</div>
                                    </div>
                                    {ev.refundPolicy.description ? (
                                        <div
                                            className="rounded-xl border border-gray-200 bg-gray-50 p-3 text-xs text-gray-700">
                                            {ev.refundPolicy.description}
                                        </div>
                                    ) : null}

                                    <div className="flex justify-end pt-2">
                                        <Button
                                            type="button"
                                            variant="secondary"
                                            size="md"
                                            onClick={() =>
                                                mode === "edit"
                                                    ? navigate(`/business/events/${encodeURIComponent(eventId)}/edit/refund-policy`)
                                                    : navigate(`/business/events/new/refund-policy?eventId=${encodeURIComponent(eventId)}`)
                                            }
                                        >
                                            Edit refund policy
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
                                    No refund policy found.
                                    <div className="mt-3">
                                        <Button
                                            type="button"
                                            variant="primary"
                                            size="md"
                                            onClick={() =>
                                                mode === "edit"
                                                    ? navigate(`/business/events/${encodeURIComponent(eventId)}/edit/refunds`)
                                                    : navigate(`/business/events/new/refunds?eventId=${encodeURIComponent(eventId)}`)
                                            }
                                        >
                                            Configure refund policy
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </Card>

                        <Card
                            title="Data collection & waiver"
                            hint="Name + email are collected by default."
                            icon={<CheckCircle2Icon className="size-4 text-gray-500"/>}
                        >
                            <div className="space-y-2 text-sm text-gray-700">
                                <div className="flex items-center justify-between">
                                    <div className="font-semibold">Attendee details per ticket</div>
                                    <div>{ev.collectAttendeeDetailsForEachTicket ? "Yes" : "No"}</div>
                                </div>
                                <div className="flex items-center justify-between">
                                    <div className="font-semibold">Medical aid</div>
                                    <div>{ev.collectMedicalAid ? "Yes" : "No"}</div>
                                </div>
                                <div className="flex items-center justify-between">
                                    <div className="font-semibold">Emergency contact</div>
                                    <div>{ev.collectEmergencyContact ? "Yes" : "No"}</div>
                                </div>
                                <div className="flex items-center justify-between">
                                    <div className="font-semibold">Membership lookup required</div>
                                    <div>{ev.membershipLookupRequired ? "Yes" : "No"}</div>
                                </div>
                                <div className="flex items-center justify-between">
                                    <div className="font-semibold">Custom fields</div>
                                    <div>{ev.customFieldCount ?? 0}</div>
                                </div>

                                <div className="pt-3">
                                    <div className="flex items-center justify-between">
                                        <div className="font-semibold">Waiver required</div>
                                        <div>{ev.waiverRequired ? "Yes" : "No"}</div>
                                    </div>

                                    {ev.waiverRequired ? (
                                        <div
                                            className="mt-2 rounded-xl border border-gray-200 bg-gray-50 p-3 text-xs text-gray-800">
                                            {ev.waiverText?.trim() ? (
                                                <MarkdownDisplay value={ev.waiverText || "Nothing to preview"}/>
                                            ) : (
                                                <div className="text-amber-700">
                                                    Waiver is required but waiver text is empty.
                                                </div>
                                            )}
                                        </div>
                                    ) : null}
                                </div>

                                <div className="flex justify-end pt-2">
                                    <Button
                                        type="button"
                                        variant="secondary"
                                        size="md"
                                        onClick={() =>
                                            mode === "edit"
                                                ? navigate(`/business/events/${encodeURIComponent(eventId)}/edit/data-collection`)
                                                : navigate(`/business/events/new/data-collection?eventId=${encodeURIComponent(eventId)}`)
                                        }
                                    >
                                        Edit data collection
                                    </Button>
                                </div>
                            </div>
                        </Card>

                        <Card
                            title="Speakers & line-up"
                            hint="Optional. Each section shows on the event page once it has entries."
                            icon={<UsersIcon className="size-4 text-gray-500"/>}
                        >
                            <div className="space-y-2 text-sm text-gray-700">
                                <div className="flex items-center justify-between">
                                    <div className="font-semibold">Speakers</div>
                                    <div>{programme ? programme.speakers : "Unavailable"}</div>
                                </div>
                                <div className="flex items-center justify-between">
                                    <div className="font-semibold">Line-up slots</div>
                                    <div>{programme ? programme.slots : "Unavailable"}</div>
                                </div>
                                <div className="flex justify-end pt-2">
                                    <Button
                                        type="button"
                                        variant="secondary"
                                        size="md"
                                        onClick={() =>
                                            mode === "edit"
                                                ? navigate(`/business/events/${encodeURIComponent(eventId)}/edit/speakers`)
                                                : navigate(`/business/events/new/speakers?eventId=${encodeURIComponent(eventId)}`)
                                        }
                                    >
                                        Edit speakers & line-up
                                    </Button>
                                </div>
                            </div>
                        </Card>

                        <Card title="Mailing messages" icon={<CheckCircle2Icon className="size-4 text-gray-500"/>}>
                            <div className="space-y-3">
                                <div>
                                    <div className="text-xs font-semibold text-gray-500">Confirmation message block
                                    </div>
                                    <div
                                        className="mt-2 rounded-xl border border-gray-200 bg-gray-50 p-3 text-sm text-gray-800">
                                        {ev.confirmationEmailText?.trim() ? (
                                            <MarkdownDisplay value={ev.confirmationEmailText || "Nothing to preview"}/>
                                        ) : (
                                            <span className="text-gray-500">Default will be used.</span>
                                        )}
                                    </div>
                                </div>

                                <div>
                                    <div className="text-xs font-semibold text-gray-500">Complimentary ticket message
                                        block
                                    </div>
                                    <div
                                        className="mt-2 rounded-xl border border-gray-200 bg-gray-50 p-3 text-sm text-gray-800">
                                        {ev.complementaryTicketEmailText?.trim() ? (
                                            <MarkdownDisplay
                                                value={ev.complementaryTicketEmailText || "Nothing to preview"}/>
                                        ) : (
                                            <span className="text-gray-500">Default will be used.</span>
                                        )}
                                    </div>
                                </div>

                                <div>
                                    <div className="text-xs font-semibold text-gray-500">Invoice message block</div>
                                    <div
                                        className="mt-2 rounded-xl border border-gray-200 bg-gray-50 p-3 text-sm text-gray-800">
                                        {ev.invoiceEmailText?.trim() ? (
                                            <MarkdownDisplay value={ev.invoiceEmailText || "Nothing to preview"}/>
                                        ) : (
                                            <span className="text-gray-500">Default will be used.</span>
                                        )}
                                    </div>
                                </div>

                                <div className="flex justify-end pt-1">
                                    <Button
                                        type="button"
                                        variant="secondary"
                                        size="md"
                                        onClick={() =>
                                            mode === "edit"
                                                ? navigate(`/business/events/${encodeURIComponent(eventId)}/edit/mailing`)
                                                : navigate(`/business/events/new/mailing?eventId=${encodeURIComponent(eventId)}`)
                                        }
                                    >
                                        Edit mailing
                                    </Button>
                                </div>
                            </div>
                        </Card>
                    </div>
                </div>
            )}
        </div>
    );
}
